import { createStore } from "@xstate/store";
import type { CalendarSettings, ScheduleView } from "@/features/scheduling/components/calendar/types";
import { DEFAULT_CALENDAR_SETTINGS } from "@/features/scheduling/components/calendar/types";

const STORAGE_KEY = "schedule-preferences";

export interface SchedulePreferences {
  date: string;
  view: ScheduleView;
  scheduleIds?: string;
  calendarSettings?: CalendarSettings;
}

/** Format date as local YYYY-MM-DD */
export function formatLocalDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function loadPrefs(): SchedulePreferences | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  return JSON.parse(stored) as SchedulePreferences;
}

/** Parse scheduleIds param into array of IDs, filtering out stale IDs not in allIds */
export function parseScheduleIds(param: string | undefined, allIds: string[]): string[] | undefined {
  if (!param || param === "") return undefined;
  if (param === "all") return allIds;
  const allSet = new Set(allIds);
  const ids = param.split(",").filter((id) => id && allSet.has(id));
  return ids.length > 0 ? ids : undefined;
}

function savePrefs(state: {
  dateStr: string;
  view: ScheduleView;
  selectedScheduleIds: string[];
  allScheduleIds: string[];
  calendarSettings: CalendarSettings;
}) {
  const serialized =
    state.selectedScheduleIds.length === state.allScheduleIds.length && state.selectedScheduleIds.length > 1
      ? "all"
      : state.selectedScheduleIds.join(",");
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      date: state.dateStr,
      view: state.view,
      scheduleIds: serialized,
      calendarSettings: state.calendarSettings,
    }),
  );
}

let _lastSyncKey = "";

function syncUrlFromStore() {
  if (!window.location.pathname.startsWith("/schedule") || window.location.pathname.startsWith("/schedule/manage")) {
    return;
  }
  const { context } = scheduleStore.get();
  if (!context._initialized) return;
  const syncKey = `${context.dateStr}|${context.view}`;
  if (syncKey === _lastSyncKey) return;
  _lastSyncKey = syncKey;
  const params = new URLSearchParams();
  params.set("date", context.dateStr);
  params.set("view", context.view);
  window.history.replaceState(window.history.state, "", `/schedule?${params.toString()}`);
}

export type TimelineMode = "practitioners" | "resources";

export const scheduleStore = createStore({
  context: {
    date: new Date(),
    dateStr: formatLocalDate(new Date()),
    view: "week" as ScheduleView,
    selectedScheduleIds: [] as string[],
    allScheduleIds: [] as string[],
    currentUserRef: null as string | null,
    calendarSettings: DEFAULT_CALENDAR_SETTINGS,
    timelineMode: "practitioners" as TimelineMode,
    _initialized: false,
  },
  on: {
    "schedule.init": (
      ctx,
      e: {
        date: Date;
        dateStr: string;
        view: ScheduleView;
        selectedScheduleIds: string[];
        allScheduleIds: string[];
        currentUserRef: string | null;
        calendarSettings: CalendarSettings;
      },
    ) => ({
      ...ctx,
      date: e.date,
      dateStr: e.dateStr,
      view: e.view,
      selectedScheduleIds: e.selectedScheduleIds,
      allScheduleIds: e.allScheduleIds,
      currentUserRef: e.currentUserRef,
      calendarSettings: e.calendarSettings,
      _initialized: true,
    }),
    "schedule.setDate": (ctx, e: { date: Date }) => ({
      ...ctx,
      date: e.date,
      dateStr: formatLocalDate(e.date),
    }),
    "schedule.setView": (ctx, e: { view: ScheduleView }) => ({
      ...ctx,
      view: e.view,
    }),
    "schedule.setSelectedIds": (ctx, e: { ids: string[] }) => {
      if (
        ctx.selectedScheduleIds.length === e.ids.length &&
        ctx.selectedScheduleIds.every((id, i) => id === e.ids[i])
      ) {
        return ctx;
      }
      return { ...ctx, selectedScheduleIds: e.ids };
    },
    "schedule.setAllIds": (ctx, e: { ids: string[] }) => {
      if (ctx.allScheduleIds.length === e.ids.length && ctx.allScheduleIds.every((id, i) => id === e.ids[i])) {
        return ctx;
      }
      // Prune selectedScheduleIds to only IDs that still exist
      const validSet = new Set(e.ids);
      const pruned = ctx.selectedScheduleIds.filter((id) => validSet.has(id));
      return {
        ...ctx,
        allScheduleIds: e.ids,
        selectedScheduleIds: pruned.length > 0 ? pruned : e.ids.length > 0 ? [e.ids[0]] : [],
      };
    },
    "schedule.setCalendarSettings": (ctx, e: { settings: CalendarSettings }) => ({
      ...ctx,
      calendarSettings: e.settings,
    }),
    "schedule.setTimelineMode": (ctx, e: { mode: TimelineMode }) => ({
      ...ctx,
      timelineMode: e.mode,
    }),
  },
});

// Global subscription: sync prefs + URL whenever store changes (after init).
// Uses window.history.replaceState instead of router.navigate to avoid
// triggering TanStack Router re-matching and useSyncExternalStore cascades.
scheduleStore.subscribe(() => {
  const { context } = scheduleStore.get();
  if (!context._initialized) return;
  savePrefs(context);
  syncUrlFromStore();
});
